import {DataField, DataRow, Dynamic, DynamicParser} from "../channel/DynamicDataParser";

enum MessageType {
    INIT = "INIT",
    DATA_UPDATE = "DATA_UPDATE",
    DATA_REMOVE = "DATA_REMOVE",
    DATA_INSERT = "DATA_INSERT",
    DATA_CLEAR_ALL = "DATA_CLEAR_ALL",
    DATA_FLUSH_ACK = "DATA_FLUSH_ACK",
    DATA_SUBSCRIPTION = "DATA_SUBSCRIPTION",
    DATA_SUBSCRIPTION_BULK = "DATA_SUBSCRIPTION_BULK",
    DELETE_SUBSCRIPTION = "DELETE_SUBSCRIPTION",
}

type DataAction = { channel: number, row: number, data: DataField[] | null };
type Subscription = { itemText: string, adoptText: string, itemID: string }
type Task = {
    id: number;
    messageType: MessageType,
    action?: DataAction,
    subscription?: Subscription
};

/** --- Data Worker - Completely Independent --- */
const dataQueue: Task[] = [];
export const channelData: Map<number, DataRow[]> = new Map();

/** --- Batched data responses --- */
/* see docs/EXTRACTED_COMMENTS.md#c802 */
type BatchedDataResponse = {
    itemID: string,
    response: {
        data: any,
        channel: number | null,
        row: number | null,
        found: boolean,
        version: number
    }
};

const pendingDataResponses: BatchedDataResponse[] = [];

/* see docs/EXTRACTED_COMMENTS.md#c789 */
const parsedTextCache: Map<string, any> = new Map();
const PARSED_TEXT_CACHE_MAX = 4000;

function parseCached(text: string): any {
    let parsed = parsedTextCache.get(text);
    if (parsed === undefined) {
        if (parsedTextCache.size >= PARSED_TEXT_CACHE_MAX) parsedTextCache.clear();
        parsed = JSON.parse(text);
        parsedTextCache.set(text, parsed);
    }
    return parsed;
}

function scheduleDataFlush() {
    emitDataBatch();
}

function emitDataBatch() {
    if (pendingDataResponses.length === 0) return;
    const batch = pendingDataResponses.splice(0, pendingDataResponses.length);

    // Try to transfer any typed arrays inside the responses
    const transferList: any[] = [];
    for (const it of batch) {
        const d = it.response?.data as any;
        if (d instanceof ArrayBuffer) transferList.push(d);
        else if (ArrayBuffer.isView(d)) transferList.push(d.buffer);
    }

    (self as DedicatedWorkerGlobalScope).postMessage({
        type: "DATA_BATCH",
        items: batch
    }, Array.from(new Set(transferList)));
}

function pushDataResponse(dr: BatchedDataResponse) {
    pendingDataResponses.push(dr);
    scheduleDataFlush();
}

self.onmessage = (e: MessageEvent<Task | any>) => {
    if (!e.data) return;
    switch (e.data.messageType) {
        case MessageType.INIT: {
            (self as DedicatedWorkerGlobalScope).postMessage({ type: "INIT_DONE" });
            return;
        }
        case MessageType.DATA_SUBSCRIPTION: {
            if (!e.data.subscription) return;
            dataQueue.push(e.data as Task);
            Promise.resolve().then(() => dataFlush());
            break;
        }
        case MessageType.DATA_SUBSCRIPTION_BULK: {
            const tasks = e.data.tasks;
            if (!tasks || tasks.length === 0) return;
            processBulk(tasks);
            break;
        }
        case MessageType.DATA_UPDATE: {
            if (!e.data.action) return;
            const {row, channel} = e.data.action;
            const channelRows: DataRow[] = channelData.get(channel) || [];
            const dataRow: DataRow = channelRows[row] || new Map<number, DataField>() as DataRow;
            if (e.data.action.data == null) {
                dataRow.clear();
            } else {
                for (let field of e.data.action.data) {
                    let data_field = field as unknown as DataField;
                    dataRow.set(data_field.field, data_field);
                }
            }
            channelRows[row] = dataRow;
            channelData.set(channel, channelRows);
            (self as DedicatedWorkerGlobalScope).postMessage({type: "DATA_PROCESSED", data: e.data.id });
            break;
        }
        case MessageType.DATA_INSERT: {
            if (!e.data.action) return;
            const {channel, row} = e.data.action!;
            let channelRows = channelData.get(channel) || [];
            const dataRow = new Map<number, DataField>();
            // data:null is a server-side padding/cleared row — insert it empty,
            // same as DATA_UPDATE treats null.
            for (const field of (e.data.action.data ?? []) as DataField[]) {
                dataRow.set(field.field, field);
            }

            if (row >= channelRows.length) {
                // see docs/EXTRACTED_COMMENTS.md#c260
                if (row > channelRows.length) channelRows.length = row;
                channelRows[row] = dataRow;
            } else {
                channelRows = [...channelRows.slice(0, row), dataRow, ...channelRows.slice(row)];
            }
            channelData.set(channel, channelRows);
            (self as DedicatedWorkerGlobalScope).postMessage({type: "DATA_PROCESSED", data: e.data.id });
            break;
        }
        case MessageType.DATA_REMOVE: {
            if (!e.data.action) return;
            const {channel, row} = e.data.action!;
            const channelRows = channelData.get(channel);
            if (channelRows) {
                if (row < 0) {
                    // Whole-channel clear (channel.reset / emptied-channel
                    // trim). A plain splice(-1, 1) would drop only the last
                    // row and leave the rest stale.
                    channelData.set(channel, []);
                } else {
                    channelRows.splice(row, 1);
                    channelData.set(channel, channelRows);
                }
            }
            (self as DedicatedWorkerGlobalScope).postMessage({type: "DATA_PROCESSED", data: e.data.id });
            break;
        }
        case MessageType.DATA_CLEAR_ALL: {
            channelData.clear();
            dataQueue.length = 0;
            pendingDataResponses.length = 0;
            parsedTextCache.clear();
            (self as DedicatedWorkerGlobalScope).postMessage({type: "DATA_PROCESSED", data: e.data.id });
            break;
        }
        case MessageType.DATA_FLUSH_ACK: {
            // see docs/EXTRACTED_COMMENTS.md#c261
            (self as DedicatedWorkerGlobalScope).postMessage({type: "DATA_PROCESSED", data: e.data.id });
            break;
        }
    }
}

/** --- Batched data processing (chunked for event loop breathing) --- */
let processingQueue = false;

async function dataFlush() {
    if (processingQueue) return; // Already processing
    processingQueue = true;

    while (dataQueue.length > 0) {
        // Process a chunk of tasks
        const chunkSize = dataQueue.length;
        for (let i = 0; i < chunkSize; i++) {
            const task = dataQueue.shift();
            if (!task) break;
            const {id, subscription} = task;
            if (!subscription) continue;
            processTask(id, subscription);
        }

        // see docs/EXTRACTED_COMMENTS.md#c262
        if (dataQueue.length > 0) {
            await new Promise(resolve => setTimeout(resolve, 0));
        }
    }
    processingQueue = false;
}


function resolveSubscription(itemText: string, adoptText: string, version: number): BatchedDataResponse["response"] {
    const dyn = new Dynamic(parseCached(itemText));
    const adopt = Object.entries(parseCached(adoptText));
    const parser = new DynamicParser(dyn, channelData, adopt);

    const rowRes = parser.getRow();
    const valueRes = parser.getValue(rowRes);

    return {
        data: valueRes ? valueRes[0] : null,
        channel: rowRes ? rowRes[1] : null,
        row: rowRes ? rowRes[0] : null,
        found: parser.isFound(),
        version
    };
}

function processTask(id: number, subscription: Subscription) {
    const {itemID, itemText, adoptText} = subscription;
    pushDataResponse({ itemID, response: resolveSubscription(itemText, adoptText, id) });
}

function processBulk(tasks: Array<{ itemID: string; itemText: string; adoptText: string; _version: number }>) {
    const results: BatchedDataResponse[] = [];
    for (let i = 0; i < tasks.length; i++) {
        const { itemID, itemText, adoptText, _version } = tasks[i];
        results.push({ itemID, response: resolveSubscription(itemText, adoptText, _version) });
    }

    const transferList: any[] = [];
    for (const it of results) {
        const d = it.response?.data as any;
        if (d instanceof ArrayBuffer) transferList.push(d);
        else if (ArrayBuffer.isView(d)) transferList.push(d.buffer);
    }
    (self as DedicatedWorkerGlobalScope).postMessage({
        type: "DATA_BATCH",
        items: results
    }, Array.from(new Set(transferList)));
}
